import JSZip from 'jszip';
import { Slice } from '../types/slice';
import { ExportConfig, ExportProgressCallback } from '../types/export';
import { SVGExporter } from './SVGExporter';

export class ZipExporter {
  private config: ExportConfig;
  private svgExporter: SVGExporter;

  constructor(config: ExportConfig) {
    this.config = config;
    this.svgExporter = new SVGExporter(config);
  }

  setConfig(config: ExportConfig): void {
    this.config = config;
    this.svgExporter.setConfig(config);
  }

  async exportSlices(
    slices: Slice[],
    modelBounds: { minX: number; maxX: number; minY: number; maxY: number },
    onProgress?: ExportProgressCallback
  ): Promise<Blob> {
    const zip = new JSZip();

    // Create a folder for the layers
    const folder = zip.folder('layers');
    if (!folder) {
      throw new Error('Failed to create folder in ZIP');
    }

    for (let i = 0; i < slices.length; i++) {
      if (onProgress) {
        onProgress({
          current: i + 1,
          total: slices.length,
          message: `Adding layer ${i + 1} of ${slices.length}`,
        });
      }

      const svg = this.svgExporter.exportSlice(slices[i], modelBounds);
      const filename = `layer_${String(i + 1).padStart(4, '0')}.svg`;
      folder.file(filename, svg);
    }

    // Add a README file
    const readme = this.generateReadme(slices, modelBounds);
    zip.file('README.txt', readme);

    // Generate ZIP blob
    const blob = await zip.generateAsync({
      type: 'blob',
      compression: 'DEFLATE',
      compressionOptions: { level: 6 },
    }, (metadata) => {
      if (onProgress && metadata.percent) {
        onProgress({
          current: Math.round(metadata.percent),
          total: 100,
          message: 'Compressing ZIP file...',
        });
      }
    });

    return blob;
  }

  private generateReadme(
    slices: Slice[],
    modelBounds: { minX: number; maxX: number; minY: number; maxY: number }
  ): string {
    const { paperSize, scale } = this.config;

    return `Millefoglie Layer Export
========================

Total Layers: ${slices.length}
Paper Size: ${paperSize.name} (${paperSize.width}mm x ${paperSize.height}mm)
Scale: ${scale}x

Model Bounds:
  X: ${modelBounds.minX.toFixed(2)}mm to ${modelBounds.maxX.toFixed(2)}mm
  Y: ${modelBounds.minY.toFixed(2)}mm to ${modelBounds.maxY.toFixed(2)}mm

Layer Details:
${slices.map((s, i) => `  Layer ${i + 1}: z = ${s.zHeight.toFixed(2)}mm, ${s.contours.length} contours`).join('\n')}

Notes:
- SVG files are ready for cutting with laser cutters or vinyl cutters
- Each layer includes a cuttable label number (if enabled) for easy identification
- Alignment rod holes (if present) are shown in blue
- Floating sections (if highlighted) may need additional support during assembly

Generated by Millefoglie - Layered Paper Craft Software
`;
  }

  async downloadZip(
    slices: Slice[],
    modelBounds: { minX: number; maxX: number; minY: number; maxY: number },
    filename: string = 'layers.zip',
    onProgress?: ExportProgressCallback
  ): Promise<void> {
    const blob = await this.exportSlices(slices, modelBounds, onProgress);

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
}
